import type { ReactElement } from 'react';
import type { GroupedAlert } from '@kirin/api-client/contracts/alerts';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useIsOwnerOrManager, useIsSupport } from '@/hooks/useRoleCheck';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import {
  AlertCommentCell,
  AlertStatusCell,
} from '@/pages/organization/tabs/DataTableAlertsTab/FeedbackCells';

interface AlertFeedbackFieldsProps {
  alert: GroupedAlert;
  organizationId: string;
}

/**
 * KIR-4440 Status and Comment at the foot of the alert detail, so they can be
 * set while reading the alert. The same cells, edit gates and flag as the
 * alerts table's columns: Status for owners and managers, Comment for everyone
 * but support. Organization alerts only — feedback has no personal scope.
 * Hidden when the alert arrived without its `feedback` fragment.
 */
export function AlertFeedbackFields({
  alert,
  organizationId,
}: AlertFeedbackFieldsProps): ReactElement | null {
  const { flags } = useFeatureFlags([flagsmithFeatureFlags.ALERT_FEEDBACK]);
  const { isOwnerOrManager } = useIsOwnerOrManager(organizationId);
  const { isSupport } = useIsSupport(organizationId);
  // Every org alert read carries `feedback` while the flag is on. One without
  // it cannot show the real verdict, and an editable default would invite
  // saving over it, so show nothing rather than a guess.
  if (flags[flagsmithFeatureFlags.ALERT_FEEDBACK] !== true || !alert.feedback)
    return null;

  return (
    <div
      data-testid="alert-feedback-fields"
      className="flex flex-wrap items-start gap-x-8 gap-y-3 border-t pt-3"
    >
      <div className="flex flex-col gap-1">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          Status
        </span>
        <AlertStatusCell
          alert={alert}
          organizationId={organizationId}
          canEdit={isOwnerOrManager}
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          Comment
        </span>
        <AlertCommentCell
          alert={alert}
          organizationId={organizationId}
          canEdit={!isSupport}
        />
      </div>
    </div>
  );
}

AlertFeedbackFields.displayName = 'AlertFeedbackFields';
